/**
 * The categorical chart palette, as CSS variable references.
 *
 * `--chart-1..6` are defined per theme in `packages/tokens/src/tokens.css` and
 * are the only colours a chart series may use. They are contrast-checked
 * (>= 3:1 against their own theme's `--bg-surface`, the WCAG 2.2 SC 1.4.11
 * floor for a meaning-carrying graphical mark) and stay separable under
 * simulated protanopia / deuteranopia / tritanopia.
 *
 * Referencing them through CSS custom properties rather than importing hex
 * from `@ezhire/tokens` is deliberate: the two themes hold different values
 * under the same name, so a JS import would freeze one theme's colour into a
 * chart that renders in both.
 *
 * **Known exemption:** `StackedBarWithReference` keeps the accent ramp
 * (`--accent-500` / `--accent-300` / `--secondary-300`) for Commit / Best case
 * / Pipeline. Those three are not a categorical axis — they are a confidence
 * ordering, and a ramp that darkens with certainty carries that meaning where
 * six unrelated hues would destroy it. A sequential scale is the right tool
 * there, the same way the day-hour heatmaps keep theirs.
 *
 * Do NOT reach for `--accent-*`, `--secondary-*`, `--success` or `--warning`
 * as series colours. Several of them sit at or under the 3:1 floor in one
 * theme, and the semantic ones assert a meaning ("this series is good") that a
 * neutral category axis does not have.
 */
export const CHART_SERIES = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
] as const;

/**
 * Colour for series index `i`.
 *
 * Wraps at 6. A chart with more than 6 series will repeat a colour and become
 * unreadable — that is a signal to bucket the categories server-side into an
 * "Other" catch-all, not to extend the palette past what was contrast-checked.
 */
export function chartSeriesColor(i: number): string {
  // `(-1) % 6` is -1 in JS, not 5, so a negative index would return undefined
  // and the non-null assertion would launder it into a `string` that Recharts
  // hands to SVG verbatim. Callers reach here with -1 whenever they colour by
  // `indexOf` on a series that is not in the canonical list.
  const n = CHART_SERIES.length;
  return CHART_SERIES[((i % n) + n) % n]!;
}

/**
 * Styles for a Recharts `<Tooltip>`.
 *
 * WHY THIS EXISTS: `contentStyle` sets the colour of the tooltip BOX, not of
 * the rows inside it. Recharts colours each item row from that series' own
 * `fill`/`stroke`, and falls back to its own dark default. So a chart whose
 * bars are painted `var(--border)` — the standard treatment for "not the point
 * of this chart" bars — rendered its tooltip text in that same near-background
 * colour, i.e. dark grey on a dark surface. It is legible on a light theme and
 * effectively invisible on a dark one, which is why it survived review.
 *
 * Reported from production on the fleet duration-lattice chart, where hovering
 * a bar produced a readable "45 days" label above an unreadable
 * "440 rentals · 1.1x its neighbours".
 *
 * `itemStyle` is the one that was missing everywhere; spreading all three
 * keeps the box, the label and the rows on the same token set.
 *
 * Usage:
 *   <Tooltip {...chartTooltipStyles} formatter={...} />
 */
export const chartTooltipStyles = {
  contentStyle: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontSize: 12,
    color: 'var(--text-primary)',
  },
  // The fix. Without this, Recharts inherits the series colour for the row.
  itemStyle: { color: 'var(--text-primary)' },
  labelStyle: { color: 'var(--text-secondary)' },
} as const;
